import React, { useMemo, useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { publicApi } from '../utils/api';
import StackConsultingPrime from '../components/StackConsultingPrime';
import { IMG } from '../lib/images';
import { useTheme } from '../contexts/ThemeContext';

const TransparencyPage = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [sortBy, setSortBy] = useState('commission');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await publicApi.getTransparencyTools();
        setRows(response.data?.items || []);
      } catch (err) {
        setError('Failed to load transparency data');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const sortedRows = useMemo(() => {
    const list = [...rows];
    const multiplier = sortDir === 'asc' ? 1 : -1;

    const getCommissionValue = (value) => {
      if (!value) return -1;
      const match = String(value).match(/(\d+(?:\.\d+)?)/);
      return match ? Number(match[1]) : -1;
    };

    list.sort((a, b) => {
      let av;
      let bv;

      if (sortBy === 'commission') {
        av = getCommissionValue(a.commission);
        bv = getCommissionValue(b.commission);
      } else if (sortBy === 'cookie') {
        av = Number(a.cookie_days || 0);
        bv = Number(b.cookie_days || 0);
      } else if (sortBy === 'category') {
        av = (a.category_name || '').toLowerCase();
        bv = (b.category_name || '').toLowerCase();
      } else {
        av = a.commission_type === 'recurring' ? 1 : 0;
        bv = b.commission_type === 'recurring' ? 1 : 0;
      }

      if (av < bv) return -1 * multiplier;
      if (av > bv) return 1 * multiplier;
      return 0;
    });

    return list;
  }, [rows, sortBy, sortDir]);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Wowrolla Commission Transparency',
    description: 'Public commission disclosure for every AI tool listed on Wowrolla.',
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: sortedRows.length,
    },
  };

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-robot" data-testid="transparency-page">
      <Helmet>
        <title>Commission Transparency | Wowrolla</title>
        <meta
          name="description"
          content="Full public commission transparency across Wowrolla: rates, cookie windows, networks, and disclosure rules by tool."
        />
        <meta property="og:title" content="Wowrolla Commission Transparency" />
        <meta
          property="og:description"
          content="See exactly how Wowrolla monetizes tools while keeping recommendation quality independent from payouts."
        />
        <meta property="og:image" content="/transparency-social-card.svg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <section
        className="relative overflow-hidden mb-6 resource-hero"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(rgba(255,249,236,0.68), rgba(247,252,255,0.82)), url(${IMG.hero.transparency})`
            : `linear-gradient(rgba(8,8,18,0.74), rgba(8,8,18,0.96)), url(${IMG.hero.transparency})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 prime-aurora-bg" />
        <div className="container mx-auto px-6 py-14 relative z-10">
          <Link to="/" className="inline-flex items-center mb-6 transition-colors text-text-secondary hover:text-cyber-yellow">
            <ArrowLeft size={16} className="mr-2" />
            Back to Home
          </Link>
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-5 bg-black/40 border border-cyber-yellow/30 rounded-full">
            <ShieldCheck size={14} className="text-cyber-yellow" />
            <span className="text-xs text-cyber-yellow uppercase tracking-widest font-mono">Full disclosure</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-black tracking-tight mb-3" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>
            Commission Transparency
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            We rank by fit for your use case. Commission rate does not affect ranking order.
          </p>
          <p className="mt-2 text-sm" style={{ color: 'var(--text-muted)' }}>
            Why we disclose this: Wowrolla is built to operate as a trusted advisor, not an ad network.
          </p>
          <div className="mt-4 border border-yellow-500/30 bg-yellow-500/5 rounded p-3 text-sm max-w-2xl">
            <p className="font-semibold mb-1" style={{ color: 'var(--cyber-yellow)' }}>🍁 Required Disclosure — Competition Bureau of Canada</p>
            <p style={{ color: 'var(--text-secondary)' }}>
              Wowrolla has a material connection to the tools listed on this page through affiliate commission arrangements.
              This relationship is required to be disclosed under the <em>Competition Act</em> (Canada) and the Competition
              Bureau's guidelines on endorsements and testimonials. All commission rates, cookie windows, and affiliate networks
              are fully disclosed in the table below.
            </p>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-16">

        <div className="terminal-grid p-4 mb-4 flex flex-wrap gap-3 items-end" data-testid="transparency-sorting-controls">
          <label className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Sort by
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="ml-2 px-3 py-2 bg-transparent border border-white/20"
              style={{ color: 'var(--text-primary)' }}
            >
              <option value="commission">Commission %</option>
              <option value="cookie">Cookie Days</option>
              <option value="category">Category</option>
              <option value="type">Recurring vs One-time</option>
            </select>
          </label>

          <button
            onClick={() => setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
            className="px-3 py-2 border"
            style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--text-primary)' }}
          >
            {sortDir === 'asc' ? 'Ascending' : 'Descending'}
          </button>
        </div>

        <div className="terminal-grid p-0 overflow-x-auto" data-testid="transparency-table-shell">
          {loading && <p className="p-6" style={{ color: 'var(--text-secondary)' }}>Loading transparency data...</p>}
          {!loading && error && <p className="p-6" style={{ color: '#ff7777' }}>{error}</p>}
          {!loading && !error && (
            <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <th className="text-left px-4 py-3" style={{ color: 'var(--text-primary)' }}>Tool</th>
                  <th className="text-left px-4 py-3" style={{ color: 'var(--text-primary)' }}>Category</th>
                  <th className="text-left px-4 py-3" style={{ color: 'var(--text-primary)' }}>Commission</th>
                  <th className="text-left px-4 py-3" style={{ color: 'var(--text-primary)' }}>Cookie Window</th>
                  <th className="text-left px-4 py-3" style={{ color: 'var(--text-primary)' }}>Network</th>
                  <th className="text-left px-4 py-3" style={{ color: 'var(--text-primary)' }}>Type</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((row) => (
                  <tr key={row.tool_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>
                      <Link to={`/tools/${row.slug}`} style={{ color: 'var(--accent-primary)' }}>{row.name}</Link>
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>{row.category_name}</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-secondary)', fontFamily: 'JetBrains Mono, monospace' }}>
                      <span className="px-2 py-1" style={{ background: 'rgba(255,232,0,0.2)', color: '#FFE800' }}>
                        {row.show_commission_public ? (row.commission || 'N/A') : (row.disclosure_text_override || 'We earn a commission')}
                      </span>
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-secondary)', fontFamily: 'JetBrains Mono, monospace' }}>{row.cookie_days || 'N/A'} days</td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-secondary)', fontFamily: 'JetBrains Mono, monospace' }}>{row.network || 'Direct'}</td>
                    <td className="px-4 py-3">
                      <span style={{ color: row.commission_type === 'recurring' ? '#39FF14' : '#FFE800', fontWeight: 700 }}>
                        {row.commission_type === 'recurring' ? 'Recurring' : 'One-time'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <StackConsultingPrime />
      </section>
    </div>
  );
};

export default TransparencyPage;

